import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useUserPreferences } from '@/_shared/hooks/useUserPreferences';

const DIGITAL_SHELF_PREFS_SCOPE = 'digital-shelf';
const DIGITAL_SHELF_FILTERS_PREF_KEY = 'filters:v1';
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export type DigitalShelfFilterOption = {
  id: number;
  name: string;
  label?: string;
  cubeId?: string | null;
  mappedCategoryL4?: Array<{
    id: number;
    name: string;
    parentId: number;
    cubeId?: string | null;
  }>;
};

export type DigitalShelfDateRangeFilter = {
  startDate: string | null;
  endDate: string | null;
};

export type DigitalShelfPreviousPeriodRangeFilter =
  DigitalShelfDateRangeFilter & {
    isCustom: boolean;
  };

export type DigitalShelfCountrySelectionMode = 'single' | 'multiple';

export type DigitalShelfFiltersState = {
  dateRange: DigitalShelfDateRangeFilter;
  previousPeriodRange: DigitalShelfPreviousPeriodRangeFilter;
  countries: DigitalShelfFilterOption[];
  countriesSingle: DigitalShelfFilterOption[];
  countriesMulti: DigitalShelfFilterOption[];
  channels: DigitalShelfFilterOption[];
  singleChannel: DigitalShelfFilterOption[];
  globalScoreCardChannels: DigitalShelfFilterOption[];
  brands: DigitalShelfFilterOption[];
  categoryL2: DigitalShelfFilterOption[];
  categoryL3: DigitalShelfFilterOption[];
  categoryL4: DigitalShelfFilterOption[];
  categoryOthersSelected: boolean;
  competitorBrands: DigitalShelfFilterOption[];
  keywords: string[];
  benchmarks: ProductHealthBenchmarkFilters;
};

export type ProductHealthBenchmarkFilters = {
  onlineAvailabilityAverageMarket: boolean;
  onlineAvailabilitySelectedBrandAverage: boolean;
  ratingBenchmark: number | null;
  reviewsBenchmark: number | null;
  // Single toggle pair shared by both the Rating trend chart's and the
  // Reviews trend chart's market-average line — matches the Ratings &
  // Reviews benchmark modal's single Settings column (Figma node
  // 5708:60987), which doesn't distinguish rating vs. review lines.
  ratingReviewsAverageWholeMarket: boolean;
  ratingReviewsAverageSelectedBrand: boolean;
  // Single toggle shared by both the Price and Price Change trend charts'
  // market-average lines — matches the Price Competitiveness benchmark
  // modal's single Settings column (Figma node 5708:61529), which doesn't
  // distinguish price vs. change lines.
  priceAverageWholeMarket: boolean;
  // No chart consumer yet (matches Figma's "Average Selected Brand" toggle,
  // same as ratingReviewsAverageSelectedBrand's initial, unwired ship).
  priceAverageSelectedBrand: boolean;
  contentOverallPassScoreBenchmark: number | null;
  contentProductTitleBenchmark: number | null;
  contentProductDescriptionBenchmark: number | null;
  contentRichMediaBenchmark: number | null;
  contentAverageMarketScore: boolean;
  // Gates Content Quality's "Avg. Selected Brand" trend-chart line.
  contentQualityAverageSelectedBrand: boolean;
  showValueOnLineChart: boolean;
};

type DigitalShelfNamedFilterKey =
  | 'countries'
  | 'channels'
  | 'brands'
  | 'categoryL2'
  | 'categoryL3'
  | 'categoryL4'
  | 'competitorBrands';
type DigitalShelfNamedFilters = Pick<
  DigitalShelfFiltersState,
  DigitalShelfNamedFilterKey
>;

type SetFiltersInput =
  | DigitalShelfFiltersState
  | ((_prev: DigitalShelfFiltersState) => DigitalShelfFiltersState);

interface DigitalShelfFiltersContextProps {
  filters: DigitalShelfFiltersState;
  hydrated: boolean;
  setFilters: (_next: SetFiltersInput) => void;
  setDateRange: (_next: DigitalShelfDateRangeFilter) => void;
  setPreviousPeriodRange: (
    _next: DigitalShelfPreviousPeriodRangeFilter
  ) => void;
  setCountries: (_next: DigitalShelfFilterOption[]) => void;
  setCountriesByMode: (
    _mode: DigitalShelfCountrySelectionMode,
    _next: DigitalShelfFilterOption[]
  ) => void;
  setChannels: (_next: DigitalShelfFilterOption[]) => void;
  setSingleChannel: (_next: DigitalShelfFilterOption[]) => void;
  setGlobalScoreCardChannels: (_next: DigitalShelfFilterOption[]) => void;
  setBrands: (_next: DigitalShelfFilterOption[]) => void;
  setCategoryL2: (_next: DigitalShelfFilterOption[]) => void;
  setCategoryL3: (_next: DigitalShelfFilterOption[]) => void;
  setCategoryL4: (_next: DigitalShelfFilterOption[]) => void;
  setCompetitorBrands: (_next: DigitalShelfFilterOption[]) => void;
  setCategoryOthersSelected: (_next: boolean) => void;
  setKeywords: (_next: string[]) => void;
  setNamedFilters: (_next: Partial<DigitalShelfNamedFilters>) => void;
  setBenchmarks: (_next: Partial<ProductHealthBenchmarkFilters>) => void;
  resetFilters: () => void;
  /**
   * Call this from explicit "Apply" handlers (filter modal Apply button, date picker
   * Apply, etc.) — there is no automatic save anywhere else.
   */
  persistFilters: () => void;
}

const createDefaultBenchmarkFilters = (): ProductHealthBenchmarkFilters => ({
  onlineAvailabilityAverageMarket: false,
  onlineAvailabilitySelectedBrandAverage: false,
  ratingBenchmark: null,
  reviewsBenchmark: null,
  ratingReviewsAverageWholeMarket: false,
  ratingReviewsAverageSelectedBrand: false,
  priceAverageWholeMarket: false,
  priceAverageSelectedBrand: false,
  contentOverallPassScoreBenchmark: null,
  contentProductTitleBenchmark: null,
  contentProductDescriptionBenchmark: null,
  contentRichMediaBenchmark: null,
  contentAverageMarketScore: false,
  contentQualityAverageSelectedBrand: false,
  showValueOnLineChart: false,
});

const createDefaultDigitalShelfFilters = (): DigitalShelfFiltersState => ({
  dateRange: {
    startDate: null,
    endDate: null,
  },
  previousPeriodRange: {
    startDate: null,
    endDate: null,
    isCustom: false,
  },
  countries: [],
  countriesSingle: [],
  countriesMulti: [],
  channels: [],
  singleChannel: [],
  globalScoreCardChannels: [],
  brands: [],
  categoryL2: [],
  categoryL3: [],
  categoryL4: [],
  categoryOthersSelected: true,
  competitorBrands: [],
  keywords: [],
  benchmarks: createDefaultBenchmarkFilters(),
});

const isValidIsoDate = (value: unknown): value is string =>
  typeof value === 'string' && ISO_DATE_PATTERN.test(value);

const sanitizeDateRange = (value: unknown): DigitalShelfDateRangeFilter => {
  const startDate = isValidIsoDate(
    (value as { startDate?: unknown })?.startDate
  )
    ? ((value as { startDate: string }).startDate as string)
    : null;
  const endDate = isValidIsoDate((value as { endDate?: unknown })?.endDate)
    ? ((value as { endDate: string }).endDate as string)
    : null;

  if (!startDate || !endDate) {
    return { startDate: null, endDate: null };
  }

  if (startDate > endDate) {
    return { startDate: endDate, endDate: endDate };
  }

  return { startDate, endDate };
};

const sanitizePreviousPeriodRange = (
  value: unknown
): DigitalShelfPreviousPeriodRangeFilter => {
  const range = sanitizeDateRange(value);

  return {
    ...range,
    isCustom:
      (value as { isCustom?: unknown })?.isCustom === true &&
      Boolean(range.startDate && range.endDate),
  };
};

const sanitizeFilterOptions = (value: unknown): DigitalShelfFilterOption[] => {
  if (!Array.isArray(value)) return [];

  const uniqueById = new Map<number, DigitalShelfFilterOption>();

  value.forEach((rawOption) => {
    const idRaw = (rawOption as { id?: unknown })?.id;
    const nameRaw = (rawOption as { name?: unknown })?.name;
    const id = typeof idRaw === 'number' ? idRaw : Number.NaN;
    const name = typeof nameRaw === 'string' ? nameRaw.trim() : '';

    if (!Number.isFinite(id) || !name) return;
    if (!uniqueById.has(id)) {
      const mappedCategoryL4Raw = (
        rawOption as {
          mappedCategoryL4?: unknown;
        }
      )?.mappedCategoryL4;
      const mappedCategoryL4 = Array.isArray(mappedCategoryL4Raw)
        ? mappedCategoryL4Raw
            .map((entry) => ({
              id:
                typeof (entry as { id?: unknown }).id === 'number'
                  ? ((entry as { id: number }).id as number)
                  : Number.NaN,
              name:
                typeof (entry as { name?: unknown }).name === 'string'
                  ? (entry as { name: string }).name.trim()
                  : '',
              parentId:
                typeof (entry as { parentId?: unknown }).parentId === 'number'
                  ? ((entry as { parentId: number }).parentId as number)
                  : Number.NaN,
              cubeId:
                typeof (entry as { cubeId?: unknown }).cubeId === 'string'
                  ? (entry as { cubeId: string }).cubeId.trim() || null
                  : null,
            }))
            .filter(
              (entry) =>
                Number.isFinite(entry.id) &&
                Number.isFinite(entry.parentId) &&
                Boolean(entry.name)
            )
        : undefined;

      uniqueById.set(id, {
        id,
        name,
        label:
          typeof (rawOption as { label?: unknown })?.label === 'string'
            ? (rawOption as { label: string }).label.trim() || undefined
            : undefined,
        cubeId:
          typeof (rawOption as { cubeId?: unknown })?.cubeId === 'string'
            ? (rawOption as { cubeId: string }).cubeId.trim() || null
            : null,
        mappedCategoryL4,
      });
    }
  });

  return Array.from(uniqueById.values());
};

const areFilterOptionsEqual = (
  left: DigitalShelfFilterOption[],
  right: DigitalShelfFilterOption[]
) =>
  left.length === right.length &&
  left.every((option, index) => {
    const other = right[index];
    return (
      other !== undefined &&
      option.id === other.id &&
      option.name === other.name &&
      option.label === other.label &&
      option.cubeId === other.cubeId &&
      JSON.stringify(option.mappedCategoryL4 ?? []) ===
        JSON.stringify(other.mappedCategoryL4 ?? [])
    );
  });

const areStringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const areDateRangesEqual = (
  left: DigitalShelfDateRangeFilter,
  right: DigitalShelfDateRangeFilter
) => left.startDate === right.startDate && left.endDate === right.endDate;

const areBenchmarkFiltersEqual = (
  left: ProductHealthBenchmarkFilters,
  right: ProductHealthBenchmarkFilters
) =>
  Object.keys(left).every(
    (key) =>
      left[key as keyof ProductHealthBenchmarkFilters] ===
      right[key as keyof ProductHealthBenchmarkFilters]
  );

const areFiltersEqual = (
  left: DigitalShelfFiltersState,
  right: DigitalShelfFiltersState
) =>
  areDateRangesEqual(left.dateRange, right.dateRange) &&
  areFilterOptionsEqual(left.countries, right.countries) &&
  areFilterOptionsEqual(left.countriesSingle, right.countriesSingle) &&
  areFilterOptionsEqual(left.countriesMulti, right.countriesMulti) &&
  areFilterOptionsEqual(left.channels, right.channels) &&
  areFilterOptionsEqual(left.singleChannel, right.singleChannel) &&
  areFilterOptionsEqual(
    left.globalScoreCardChannels,
    right.globalScoreCardChannels
  ) &&
  areFilterOptionsEqual(left.brands, right.brands) &&
  areFilterOptionsEqual(left.categoryL2, right.categoryL2) &&
  areFilterOptionsEqual(left.categoryL3, right.categoryL3) &&
  areFilterOptionsEqual(left.categoryL4, right.categoryL4) &&
  areFilterOptionsEqual(left.competitorBrands, right.competitorBrands) &&
  left.categoryOthersSelected === right.categoryOthersSelected &&
  areStringArraysEqual(left.keywords, right.keywords) &&
  areBenchmarkFiltersEqual(left.benchmarks, right.benchmarks);

const sanitizeKeywordOptions = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const unique = new Set<string>();
  value.forEach((raw) => {
    if (typeof raw !== 'string') return;
    const normalized = raw.trim();
    if (!normalized) return;
    unique.add(normalized);
  });
  return Array.from(unique);
};

const sanitizeBoolean = (value: unknown): boolean => value === true;

const sanitizeNumberOrNull = (
  value: unknown,
  options: { min?: number; max?: number; decimals?: number } = {}
): number | null => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN;

  if (!Number.isFinite(parsed)) return null;

  const { min, max, decimals } = options;

  let next = parsed;

  if (typeof min === 'number') next = Math.max(min, next);
  if (typeof max === 'number') next = Math.min(max, next);
  if (typeof decimals === 'number') {
    const factor = 10 ** decimals;
    next = Math.round(next * factor) / factor;
  }

  return next;
};

const sanitizeBenchmarkFilters = (
  value: unknown
): ProductHealthBenchmarkFilters => {
  const raw = (value as Partial<ProductHealthBenchmarkFilters>) ?? {};

  return {
    onlineAvailabilityAverageMarket: sanitizeBoolean(
      raw.onlineAvailabilityAverageMarket
    ),
    onlineAvailabilitySelectedBrandAverage: sanitizeBoolean(
      raw.onlineAvailabilitySelectedBrandAverage
    ),
    ratingBenchmark: sanitizeNumberOrNull(raw.ratingBenchmark, {
      min: 1,
      max: 5,
      decimals: 1,
    }),
    reviewsBenchmark: sanitizeNumberOrNull(raw.reviewsBenchmark, {
      min: 1,
      decimals: 0,
    }),
    ratingReviewsAverageWholeMarket: sanitizeBoolean(
      raw.ratingReviewsAverageWholeMarket
    ),
    ratingReviewsAverageSelectedBrand: sanitizeBoolean(
      raw.ratingReviewsAverageSelectedBrand
    ),
    priceAverageWholeMarket: sanitizeBoolean(raw.priceAverageWholeMarket),
    priceAverageSelectedBrand: sanitizeBoolean(raw.priceAverageSelectedBrand),
    contentOverallPassScoreBenchmark: sanitizeNumberOrNull(
      raw.contentOverallPassScoreBenchmark,
      { min: 1, max: 100, decimals: 2 }
    ),
    contentProductTitleBenchmark: sanitizeNumberOrNull(
      raw.contentProductTitleBenchmark,
      { min: 1, max: 100, decimals: 2 }
    ),
    contentProductDescriptionBenchmark: sanitizeNumberOrNull(
      raw.contentProductDescriptionBenchmark,
      { min: 1, max: 100, decimals: 2 }
    ),
    contentRichMediaBenchmark: sanitizeNumberOrNull(
      raw.contentRichMediaBenchmark,
      {
        min: 1,
        max: 100,
        decimals: 2,
      }
    ),
    contentAverageMarketScore: sanitizeBoolean(raw.contentAverageMarketScore),
    contentQualityAverageSelectedBrand: sanitizeBoolean(
      raw.contentQualityAverageSelectedBrand
    ),
    showValueOnLineChart: sanitizeBoolean(raw.showValueOnLineChart),
  };
};

const sanitizeFilters = (value: unknown): DigitalShelfFiltersState => {
  const raw = (value as Record<string, unknown>) ?? {};
  const legacyCountries = sanitizeFilterOptions(raw.countries);
  const countriesSingle = sanitizeFilterOptions(
    raw.countriesSingle ?? raw.countries
  );
  const countriesMulti = sanitizeFilterOptions(
    raw.countriesMulti ?? raw.countries
  );

  return {
    ...createDefaultDigitalShelfFilters(),
    dateRange: sanitizeDateRange(raw.dateRange),
    previousPeriodRange: sanitizePreviousPeriodRange(raw.previousPeriodRange),
    countries:
      countriesSingle.length > 0
        ? countriesSingle
        : legacyCountries.length > 0
          ? legacyCountries
          : countriesMulti,
    countriesSingle,
    countriesMulti,
    channels: sanitizeFilterOptions(raw.channels),
    singleChannel: sanitizeFilterOptions(raw.singleChannel ?? raw.channels),
    globalScoreCardChannels: sanitizeFilterOptions(
      raw.globalScoreCardChannels ?? raw.channels
    ),
    brands: sanitizeFilterOptions(raw.brands),
    categoryL2: sanitizeFilterOptions(raw.categoryL2 ?? raw.categories),
    categoryL3: sanitizeFilterOptions(raw.categoryL3 ?? raw.subCategories),
    categoryL4: sanitizeFilterOptions(raw.categoryL4),
    categoryOthersSelected: raw.categoryOthersSelected !== false,
    competitorBrands: sanitizeFilterOptions(raw.competitorBrands),
    keywords: sanitizeKeywordOptions(raw.keywords),
    benchmarks: sanitizeBenchmarkFilters(raw.benchmarks),
  };
};

const DigitalShelfFiltersContext = createContext<
  DigitalShelfFiltersContextProps | undefined
>(undefined);

export const DigitalShelfFiltersProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [filters, setFiltersState] = useState<DigitalShelfFiltersState>(
    createDefaultDigitalShelfFilters
  );
  const [hydrated, setHydrated] = useState(false);
  const dirtyRef = useRef(false);

  const {
    preferences: dbPreferences,
    setPreference: setDbPreference,
    isReady: dbReady,
  } = useUserPreferences(DIGITAL_SHELF_PREFS_SCOPE);

  useEffect(() => {
    if (!dbReady || hydrated) return;
    const record = dbPreferences[DIGITAL_SHELF_FILTERS_PREF_KEY];
    if (record && record.value) {
      const nextFilters = sanitizeFilters(record.value);
      setFiltersState(nextFilters);
    }
    setHydrated(true);
  }, [dbReady, dbPreferences, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    setDbPreference(DIGITAL_SHELF_FILTERS_PREF_KEY, filters).catch(() => {
      /* error already logged inside the hook */
    });
  }, [filters, hydrated, setDbPreference]);

  const persistFilters = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  const setFilters = useCallback((next: SetFiltersInput) => {
    setFiltersState((prev) => {
      const resolvedNext = typeof next === 'function' ? next(prev) : next;
      const sanitizedNext = sanitizeFilters(resolvedNext);
      return areFiltersEqual(prev, sanitizedNext) ? prev : sanitizedNext;
    });
  }, []);

  const setDateRange = useCallback((next: DigitalShelfDateRangeFilter) => {
    const sanitizedNext = sanitizeDateRange(next);
    setFiltersState((prev) =>
      areDateRangesEqual(prev.dateRange, sanitizedNext)
        ? prev
        : {
            ...prev,
            dateRange: sanitizedNext,
          }
    );
  }, []);

  const setPreviousPeriodRange = useCallback(
    (next: DigitalShelfPreviousPeriodRangeFilter) => {
      setFiltersState((prev) => ({
        ...prev,
        previousPeriodRange: sanitizePreviousPeriodRange(next),
      }));
    },
    []
  );

  const setCountries = useCallback((next: DigitalShelfFilterOption[]) => {
    const sanitizedNext = sanitizeFilterOptions(next);
    setFiltersState((prev) =>
      areFilterOptionsEqual(prev.countries, sanitizedNext) &&
      areFilterOptionsEqual(prev.countriesSingle, sanitizedNext)
        ? prev
        : {
            ...prev,
            countries: sanitizedNext,
            countriesSingle: sanitizedNext,
          }
    );
  }, []);

  const setCountriesByMode = useCallback(
    (
      mode: DigitalShelfCountrySelectionMode,
      next: DigitalShelfFilterOption[]
    ) => {
      const sanitizedNext = sanitizeFilterOptions(next);
      setFiltersState((prev) => {
        const nextCountriesSingle =
          mode === 'single' ? sanitizedNext : prev.countriesSingle;
        const nextCountriesMulti =
          mode === 'multiple' ? sanitizedNext : prev.countriesMulti;

        if (
          areFilterOptionsEqual(prev.countries, sanitizedNext) &&
          areFilterOptionsEqual(prev.countriesSingle, nextCountriesSingle) &&
          areFilterOptionsEqual(prev.countriesMulti, nextCountriesMulti)
        ) {
          return prev;
        }

        return {
          ...prev,
          countries: sanitizedNext,
          countriesSingle: nextCountriesSingle,
          countriesMulti: nextCountriesMulti,
        };
      });
    },
    []
  );

  const setChannels = useCallback((next: DigitalShelfFilterOption[]) => {
    const sanitizedNext = sanitizeFilterOptions(next);
    setFiltersState((prev) =>
      areFilterOptionsEqual(prev.channels, sanitizedNext)
        ? prev
        : {
            ...prev,
            channels: sanitizedNext,
          }
    );
  }, []);

  const setSingleChannel = useCallback((next: DigitalShelfFilterOption[]) => {
    const sanitizedNext = sanitizeFilterOptions(next);
    setFiltersState((prev) =>
      areFilterOptionsEqual(prev.singleChannel, sanitizedNext)
        ? prev
        : {
            ...prev,
            singleChannel: sanitizedNext,
          }
    );
  }, []);

  const setGlobalScoreCardChannels = useCallback(
    (next: DigitalShelfFilterOption[]) => {
      const sanitizedNext = sanitizeFilterOptions(next);
      setFiltersState((prev) =>
        areFilterOptionsEqual(prev.globalScoreCardChannels, sanitizedNext)
          ? prev
          : {
              ...prev,
              globalScoreCardChannels: sanitizedNext,
            }
      );
    },
    []
  );

  const setBrands = useCallback((next: DigitalShelfFilterOption[]) => {
    const sanitizedNext = sanitizeFilterOptions(next);
    setFiltersState((prev) =>
      areFilterOptionsEqual(prev.brands, sanitizedNext)
        ? prev
        : {
            ...prev,
            brands: sanitizedNext,
          }
    );
  }, []);

  const setCategoryL2 = useCallback((next: DigitalShelfFilterOption[]) => {
    const sanitizedNext = sanitizeFilterOptions(next);
    setFiltersState((prev) =>
      areFilterOptionsEqual(prev.categoryL2, sanitizedNext)
        ? prev
        : {
            ...prev,
            categoryL2: sanitizedNext,
          }
    );
  }, []);

  const setCategoryL3 = useCallback((next: DigitalShelfFilterOption[]) => {
    const sanitizedNext = sanitizeFilterOptions(next);
    setFiltersState((prev) =>
      areFilterOptionsEqual(prev.categoryL3, sanitizedNext)
        ? prev
        : {
            ...prev,
            categoryL3: sanitizedNext,
          }
    );
  }, []);

  const setCategoryL4 = useCallback((next: DigitalShelfFilterOption[]) => {
    const sanitizedNext = sanitizeFilterOptions(next);
    setFiltersState((prev) =>
      areFilterOptionsEqual(prev.categoryL4, sanitizedNext)
        ? prev
        : {
            ...prev,
            categoryL4: sanitizedNext,
          }
    );
  }, []);

  const setCompetitorBrands = useCallback(
    (next: DigitalShelfFilterOption[]) => {
      const sanitizedNext = sanitizeFilterOptions(next);
      setFiltersState((prev) =>
        areFilterOptionsEqual(prev.competitorBrands, sanitizedNext)
          ? prev
          : {
              ...prev,
              competitorBrands: sanitizedNext,
            }
      );
    },
    []
  );

  const setCategoryOthersSelected = useCallback((next: boolean) => {
    setFiltersState((prev) =>
      prev.categoryOthersSelected === next
        ? prev
        : {
            ...prev,
            categoryOthersSelected: next,
          }
    );
  }, []);

  const setKeywords = useCallback((next: string[]) => {
    const sanitizedNext = sanitizeKeywordOptions(next);
    setFiltersState((prev) =>
      areStringArraysEqual(prev.keywords, sanitizedNext)
        ? prev
        : {
            ...prev,
            keywords: sanitizedNext,
          }
    );
  }, []);

  const setNamedFilters = useCallback(
    (next: Partial<DigitalShelfNamedFilters>) => {
      setFiltersState((prev) => {
        const nextCountries =
          next.countries !== undefined
            ? sanitizeFilterOptions(next.countries)
            : prev.countries;
        const nextCountriesSingle =
          next.countries !== undefined ? nextCountries : prev.countriesSingle;
        const nextCountriesMulti =
          next.countries !== undefined ? nextCountries : prev.countriesMulti;
        const nextChannels =
          next.channels !== undefined
            ? sanitizeFilterOptions(next.channels)
            : prev.channels;
        const nextSingleChannel =
          next.channels !== undefined ? nextChannels : prev.singleChannel;
        const nextBrands =
          next.brands !== undefined
            ? sanitizeFilterOptions(next.brands)
            : prev.brands;
        const nextCategoryL2 =
          next.categoryL2 !== undefined
            ? sanitizeFilterOptions(next.categoryL2)
            : prev.categoryL2;
        const nextCategoryL3 =
          next.categoryL3 !== undefined
            ? sanitizeFilterOptions(next.categoryL3)
            : prev.categoryL3;
        const nextCategoryL4 =
          next.categoryL4 !== undefined
            ? sanitizeFilterOptions(next.categoryL4)
            : prev.categoryL4;
        const nextCompetitorBrands =
          next.competitorBrands !== undefined
            ? sanitizeFilterOptions(next.competitorBrands)
            : prev.competitorBrands;

        if (
          areFilterOptionsEqual(prev.countries, nextCountries) &&
          areFilterOptionsEqual(prev.countriesSingle, nextCountriesSingle) &&
          areFilterOptionsEqual(prev.countriesMulti, nextCountriesMulti) &&
          areFilterOptionsEqual(prev.channels, nextChannels) &&
          areFilterOptionsEqual(prev.singleChannel, nextSingleChannel) &&
          areFilterOptionsEqual(prev.brands, nextBrands) &&
          areFilterOptionsEqual(prev.categoryL2, nextCategoryL2) &&
          areFilterOptionsEqual(prev.categoryL3, nextCategoryL3) &&
          areFilterOptionsEqual(prev.categoryL4, nextCategoryL4) &&
          areFilterOptionsEqual(prev.competitorBrands, nextCompetitorBrands)
        ) {
          return prev;
        }

        return {
          ...prev,
          countries: nextCountries,
          countriesSingle: nextCountriesSingle,
          countriesMulti: nextCountriesMulti,
          channels: nextChannels,
          singleChannel: nextSingleChannel,
          brands: nextBrands,
          categoryL2: nextCategoryL2,
          categoryL3: nextCategoryL3,
          categoryL4: nextCategoryL4,
          competitorBrands: nextCompetitorBrands,
        };
      });
    },
    []
  );

  const setBenchmarks = useCallback(
    (next: Partial<ProductHealthBenchmarkFilters>) => {
      setFiltersState((prev) => {
        const benchmarks = sanitizeBenchmarkFilters({
          ...prev.benchmarks,
          ...next,
        });

        return areBenchmarkFiltersEqual(prev.benchmarks, benchmarks)
          ? prev
          : {
              ...prev,
              benchmarks,
            };
      });
    },
    []
  );
  const resetFilters = useCallback(() => {
    setFiltersState(createDefaultDigitalShelfFilters());
  }, []);

  const contextValue = useMemo(
    () => ({
      filters,
      hydrated,
      setFilters,
      setDateRange,
      setPreviousPeriodRange,
      setCountries,
      setCountriesByMode,
      setChannels,
      setSingleChannel,
      setGlobalScoreCardChannels,
      setBrands,
      setCategoryL2,
      setCategoryL3,
      setCategoryL4,
      setCompetitorBrands,
      setCategoryOthersSelected,
      setKeywords,
      setNamedFilters,
      setBenchmarks,
      resetFilters,
      persistFilters,
    }),
    [
      filters,
      hydrated,
      setFilters,
      setDateRange,
      setPreviousPeriodRange,
      setCountries,
      setCountriesByMode,
      setChannels,
      setSingleChannel,
      setGlobalScoreCardChannels,
      setBrands,
      setCategoryL2,
      setCategoryL3,
      setCategoryL4,
      setCompetitorBrands,
      setCategoryOthersSelected,
      setKeywords,
      setNamedFilters,
      setBenchmarks,
      resetFilters,
      persistFilters,
    ]
  );

  return (
    <DigitalShelfFiltersContext.Provider value={contextValue}>
      {children}
    </DigitalShelfFiltersContext.Provider>
  );
};

export const usePersistentDigitalShelfFilters =
  (): DigitalShelfFiltersContextProps => {
    const context = useContext(DigitalShelfFiltersContext);
    if (!context) {
      throw new Error(
        'usePersistentDigitalShelfFilters must be used within a DigitalShelfFiltersProvider'
      );
    }
    return context;
  };
